import { GtmDataSyncLib } from "../shared/GtmDataSyncLib";
import { inject, injectable, singleton } from "tsyringe";
import { UserManager } from "../shared/UserManager";
import { SiteRules } from "../shared/SiteRules";
import { Logger } from "../utils/Logger";
import { LotameService } from "./lotame/LotameService";
import { StreamMediaType } from "../shared/StreamMediaType";
import { onConsentChangeComplete, invokeWhenProfileSetup } from "../cdp/BlueConicUtils";
import { DataSyncLibEventNames } from "../shared/DataSyncLibEventNames";
import { ToutvCdpClient } from "./ToutvCdpClient";
import { CmpService } from "../cmp/CmpService";

@singleton()
@injectable()
export class ToutvLib extends GtmDataSyncLib {
  // The public properties are accessed by GTM tags.
  constructor(
    @inject(ToutvCdpClient) public cdpClient: ToutvCdpClient,
    @inject(CmpService) public cmpService: CmpService,
    @inject(LotameService) public lotameService: LotameService,
    @inject(UserManager) private userManager: UserManager,
    @inject(SiteRules) private siteRules: SiteRules,
  ) {
    super();
  }

  /**
   * We are dependent on multiple triggers in GTM:
   * - aaTrackPageView: a page view occurred. Occurs on every page view.
   * - userMetadataLoaded: the information about the current user is available. This event can occurs
   *   more than once, it's filtered using trigger : userMetadataAvailable.
   * We can't do anything until the userMetadataLoaded event is triggered.
   * Therefore, this flag indicate whether the userMetadataLoaded event occurred and user information is available.
   */
  private _userInitialized = false;

  /** Since the user initialization process is asynchronous, this flag is used
   * to prevent the process from running more than once.
   */
  private _userInitializationStarted = false;

  /**
   * Used to check if it's the first pageView. Some behaviors need to be sent only on subsequent page views.
   */
  private isFirstLoad = true;

  public async setupCurrentUser(planName?: string, discountName?: string): Promise<void> {
    Logger.verbose("setupCurrentUser");
    this.validateStorageAccess();
    if (this._userInitialized) {
      // The event userMetadataLoaded event is called at a 5h50m interval if the page is left idle.
      return;
    } else if (!this._userInitializationStarted) {
      this._userInitializationStarted = true;
      if (!this.siteRules.isGTMLibraryEnabled) {
        Logger.verbose("Tracking disabled. Aborting.");
        return;
      }

      const isCmpEnabled = this.cmpService.isCmpEnabled();
      if (isCmpEnabled) {
        Logger.verbose("Waiting for consent change handled");
        onConsentChangeComplete(async () => {
          await this.initializeProviders(planName, discountName);
        });
      } else {
        await this.initializeProviders(planName, discountName);
      }
    }
  }

  async initializeProviders(planName?: string, discountName?: string): Promise<void> {
    if (typeof planName !== "undefined" && typeof discountName !== "undefined") {
      this.lotameService.initialize(planName, discountName);
    }

    await this.cdpClient.initialize();
    invokeWhenProfileSetup(async () => {
      await this.userManager.initDataLayerUserAndUIE();
      this._userInitialized = true;
      window.dispatchEvent(new Event(DataSyncLibEventNames.UserInitialized));
    });
  }

  public async handlePageView(): Promise<void> {
    Logger.verbose("handlePageView");
    this.invokeWhenUserInitialized(async () => {
      if (!this.siteRules.isGTMLibraryEnabled) {
        Logger.verbose("Tracking disabled, aborting.");
        return;
      }
      if (!this.siteRules.isTrackingEnabled) {
        Logger.verbose("Tracking disabled: aborting handlePageView");
        return;
      }

      await this.cdpClient.handlePageView();
      // Lotame PageView is already called after Lotame initialization in setupCurrentUser
      if (!this.isFirstLoad) {
        this.lotameService.handlePageView();
      } else {
        this.isFirstLoad = false;
      }
    });
  }

  /**
   * Handle the event raised when a user completed a subscription to tou.tv Extra.
   * Called by the tag dataSync_handle_subscriptionCompleted in GTM
   * @param planName
   * The subscription plan name of the current user.
   * @param discountName
   * The subscription discount name of the current user.
   */
  // noinspection JSUnusedGlobalSymbols
  public handleSubscriptionCompleted(planName: string, discountName: string): void {
    Logger.verbose("handleSubscriptionCompleted");
    this.invokeWhenUserInitialized(async () => {
      this.lotameService.sendSubscriptionComplete(planName, discountName);
      this.cdpClient.handleSubscriptionCompleted();
    });
  }

  setLotameIdInUie(/*lotameId: string*/): Promise<void> {
    // Directly implemented in LotameClient (see method lotameLightningTagSetup)
    throw "Not Implemented";
  }

  handleReadEvent(): Promise<void> {
    // There is no read event in tou.tv
    throw "Not Implemented";
  }

  public async handleConsentChanged(): Promise<void> {
    Logger.verbose("handleConsentChanged");
    this.cmpService.fetchConsent();
    this.cdpClient.processConsentChange();
    window.dispatchEvent(new Event(DataSyncLibEventNames.ConsentChangeHandled));
  }

  public async handleContentStart(
    planName?: string,
    discountName?: string,
    mediaType: StreamMediaType = StreamMediaType.Video,
  ): Promise<void> {
    Logger.verbose("handleContentStart");
    this.invokeWhenUserInitialized(async () => {
      if (!this.siteRules.isTrackingEnabled) {
        Logger.verbose("Tracking disabled: aborting handleContentStart");
        return;
      }
      if (typeof planName !== "undefined" && typeof discountName !== "undefined") {
        this.lotameService.handleContentStart(planName, discountName);
      }
      await this.cdpClient.sendRcStream(mediaType);
    });
  }

  private invokeWhenUserInitialized(callback: () => void): void {
    if (this._userInitialized) {
      callback();
    } else {
      window.addEventListener(DataSyncLibEventNames.UserInitialized, callback);
    }
  }
}
